import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Injectable, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import {
  IAuthFuncPermissaoResponse,
  IForgotPassword,
  ILoginEmpresa,
  ILoginFuncionario
} from './auth.model';
import { UserIpService } from '../UserIpService/userIp.service';
import { ModuloService } from '../ModuloService/modulo.service';
import { PermissaoService } from '../PermissaoService/permissao.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _isTokenValid: boolean = false;
  private ip: string = '';
  private readonly API_URL = `${environment.baseURL}Empresa/LoginEmpresa`;
  private readonly API_URL_FUNC = `${environment.baseURL}Funcionario/LoginFuncionario`;
  private dadoLogin = new BehaviorSubject({
    idEmpresa: '',
    nome: '',
    Token: '',
    RefreshToken: '',
    RefreshTokenExpireDate: '',
    role: ''
  });
  dadoLogin$ = this.dadoLogin.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private snackBar: MatSnackBar,
    private userIpService: UserIpService,
    private moduloService: ModuloService,
    private permissaoService: PermissaoService
  ) {
    this.getIpAddress();

    const rawDadoLogin = localStorage.getItem('DADO_LOGIN');
    if (!rawDadoLogin) {
      return;
    }

    this.dadoLogin.next(JSON.parse(rawDadoLogin));
  }

  get isTokenValid(): boolean {
    return this._isTokenValid;
  }

  loginFunc(email: string, password: string, modal?: boolean) {
    return this.postFuncionario(email, password, modal);
  }

  postFuncionario(email: string, password: string, modal?: boolean) {
    let body: ILoginFuncionario | any = {};
    if (!email.includes('@')) {
      body = {
        TX_USER_NAME: email,
        TX_SENH_FUNC: password
      };
    }
    if (email.includes('@')) {
      body = {
        TX_MAIL_FUNC: email,
        TX_SENH_FUNC: password
      };
    }

    return this.http
      .post<{
        IdRole?: number;
        NmRole?: string;
        IdFuncionario?: number;
        NmFuncionario?: string;
        NmEmail?: string;
        Token?: string;
        RefreshToken?: string;
        RefreshTokenExpireDate?: string;
        PermissoesList?: IAuthFuncPermissaoResponse[];
      }>(this.API_URL_FUNC, body)
      .pipe(
        tap(response => {
          if (modal) {
            return;
          }
          this.saveToken(response.Token ?? '');
          localStorage.setItem('nome', response.NmFuncionario ?? '');
          localStorage.setItem('roleFuncionario', response.NmRole ?? '');

          localStorage.setItem('ipv4', this.ip);
          localStorage.setItem('id', String(response.IdFuncionario) ?? '');
          localStorage.setItem(
            'permissao',
            JSON.stringify(response?.PermissoesList ?? [])
          );
          localStorage.setItem('role', 'interno');
          this._isTokenValid = this.isTokenValidToken(
            response.Token ?? ''
          ).valid;

          this.moduloService.getModulosStatus().subscribe();

          return response;
        }),
        catchError(error => {
          return throwError(error);
        })
      );
  }
  logout(): void {
    this.removeToken();
    this._isTokenValid = false;
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    this._isTokenValid = token !== null && this.isTokenValidToken(token).valid;
    return this._isTokenValid;
  }

  private saveToken(token: string): void {
    localStorage.setItem('token', token);
  }

  private removeToken(): void {
    localStorage.removeItem('token');
  }

  private isTokenValidToken(token: string): {
    valid: boolean;
    tokenDecoded?: any;
  } {
    try {
      const tokenDecoded = jwtDecode<{ exp: number }>(token);
      const currentTime = new Date().getTime() / 1000;
      return { valid: tokenDecoded.exp > currentTime, tokenDecoded };
    } catch {
      return { valid: false };
    }
  }

  isAuthenticatedAndTokenValid() {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    const tokenData = this.getTokenData(token);
    const expiration = new Date(tokenData.exp * 1000);

    return expiration > new Date();
  }

  private getTokenData(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const decodedData = JSON.parse(atob(base64));
    return decodedData;
  }

  public forgotPassword(body: IForgotPassword): Observable<any> {
    return this.http.post<any>(
      `${environment.baseURL}Email/SendForgotPasswordEmail`,
      body
    );
  }

  refreshPermissoes(): Observable<any> {
    const userId = localStorage.getItem('id');
    if (!userId) {
      return new Observable(subscriber => subscriber.complete());
    }

    return this.permissaoService.GetPermissoesByFuncionario(Number(userId)).pipe(
      tap(permissoes => {
        localStorage.setItem('permissao', JSON.stringify(permissoes ?? []));
      }),
      catchError(error => {
        return throwError(error);
      })
    );
  }

  getIpAddress(): void {
    const res = this.userIpService.getIpAddress().subscribe({
      next: (res: any) => {
        this.ip = res.ip;
      }
    });
  }
}
